import React, { useState, useEffect } from 'react';
import { User, Shield, Key, Upload, CheckCircle, AlertTriangle, Clock, XCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import { useAuthStore } from '../store/authStore';
import api from '../services/api';
import toast from 'react-hot-toast';

const ProfilePage = () => {
    const { user, checkAuth } = useAuthStore();
    const [activeTab, setActiveTab] = useState('profile');
    const [loading, setLoading] = useState(false);
    const [kycStatus, setKycStatus] = useState(null);

    const [formData, setFormData] = useState({
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        phone: user?.phone || '',
        address: '',
        city: '',
        postalCode: '',
        country: '',
        documentType: 'passport',
        documentNumber: ''
    });

    const [files, setFiles] = useState({
        front: null,
        back: null,
        selfie: null
    });

    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    useEffect(() => {
        fetchKYCStatus();
    }, []);

    const fetchKYCStatus = async () => {
        try {
            const res = await api.get('/user/kyc/status');
            setKycStatus(res.data.kyc);
            if (res.data.kyc) {
                setFormData(prev => ({
                    ...prev,
                    address: res.data.kyc.address || '',
                    city: res.data.kyc.city || '',
                    postalCode: res.data.kyc.postalCode || '',
                    country: res.data.kyc.country || ''
                }));
            }
        } catch (error) {
            console.error('Error fetching KYC status:', error);
        }
    };

    const handleUpdateProfile = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.put('/user/profile', {
                firstName: formData.firstName,
                lastName: formData.lastName,
                phone: formData.phone
            });
            toast.success('Profile updated successfully');
            // Refresh page to show updated data
            window.location.reload();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to update profile');
        } finally {
            setLoading(false);
        }
    };

    const handleKYCSubmit = async (e) => {
        e.preventDefault();

        // File size validation (2MB = 2 * 1024 * 1024 bytes)
        const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB

        const checkFileSize = (file, name) => {
            if (file && file.size > MAX_FILE_SIZE) {
                const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
                toast.error(`${name} file is too large (${sizeMB}MB). Maximum size is 2MB.`);
                return false;
            }
            return true;
        };

        // Check all files
        if (!checkFileSize(files.front, 'Front ID')) return;
        if (!checkFileSize(files.back, 'Back ID')) return;
        if (!checkFileSize(files.selfie, 'Selfie')) return;

        setLoading(true);

        const formDataObj = new FormData();
        Object.keys(formData).forEach(key => {
            if (['address', 'city', 'postalCode', 'country', 'documentType', 'documentNumber'].includes(key)) {
                formDataObj.append(key, formData[key]);
            }
        });

        // Add files
        if (files.front) formDataObj.append('documentFront', files.front);
        if (files.back) formDataObj.append('documentBack', files.back);
        if (files.selfie) formDataObj.append('selfie', files.selfie);

        try {
            await api.post('/user/kyc/submit', formDataObj, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success('KYC submitted successfully');
            fetchKYCStatus();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to submit KYC');
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            return toast.error('Passwords do not match');
        }
        setLoading(true);
        try {
            await api.put('/user/change-password', passwordData);
            toast.success('Password changed successfully');
            setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to change password');
        } finally {
            setLoading(false);
        }
    };

    const renderStatusBadge = (status) => {
        switch (status) {
            case 'verified':
            case 'approved':
                return <span className="flex items-center gap-1 text-green-500 bg-green-500/10 px-3 py-1 rounded-full text-sm"><CheckCircle size={14} /> Verified</span>;
            case 'pending':
            case 'under_review':
                return <span className="flex items-center gap-1 text-yellow-500 bg-yellow-500/10 px-3 py-1 rounded-full text-sm"><Clock size={14} /> Under Review</span>;
            case 'rejected':
                return <span className="flex items-center gap-1 text-red-500 bg-red-500/10 px-3 py-1 rounded-full text-sm"><XCircle size={14} /> Rejected</span>;
            default:
                return <span className="flex items-center gap-1 text-gray-400 bg-gray-700/50 px-3 py-1 rounded-full text-sm"><AlertTriangle size={14} /> Not Verified</span>;
        }
    };

    return (
        <div className="bg-[#181a20] min-h-screen text-gray-100 font-sans">
            <Navbar />
            <div className="pt-8 pb-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-4xl mx-auto">
                    <h1 className="text-2xl font-bold text-white mb-8">Account Settings</h1>

                    <div className="bg-[#1e2329] rounded-xl border border-gray-800 overflow-hidden">
                        <div className="flex border-b border-gray-800 overflow-x-auto">
                            <button
                                onClick={() => setActiveTab('profile')}
                                className={`flex items-center gap-2 px-4 sm:px-6 py-4 text-xs sm:text-sm font-medium transition whitespace-nowrap ${activeTab === 'profile' ? 'text-[#fcd535] border-b-2 border-[#fcd535]' : 'text-gray-400 hover:text-white'}`}
                            >
                                <User size={18} /> Profile
                            </button>
                            <button
                                onClick={() => setActiveTab('kyc')}
                                className={`flex items-center gap-2 px-4 sm:px-6 py-4 text-xs sm:text-sm font-medium transition whitespace-nowrap ${activeTab === 'kyc' ? 'text-[#fcd535] border-b-2 border-[#fcd535]' : 'text-gray-400 hover:text-white'}`}
                            >
                                <Shield size={18} /> KYC Verification
                            </button>
                            <button
                                onClick={() => setActiveTab('security')}
                                className={`flex items-center gap-2 px-4 sm:px-6 py-4 text-xs sm:text-sm font-medium transition whitespace-nowrap ${activeTab === 'security' ? 'text-[#fcd535] border-b-2 border-[#fcd535]' : 'text-gray-400 hover:text-white'}`}
                            >
                                <Key size={18} /> Security
                            </button>
                        </div>

                        <div className="p-6">
                            {/* PROFILE TAB */}
                            {activeTab === 'profile' && (
                                <form onSubmit={handleUpdateProfile} className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-400 mb-2">First Name</label>
                                            <input
                                                type="text"
                                                className="w-full bg-[#2b3139] border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#fcd535]"
                                                value={formData.firstName}
                                                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-400 mb-2">Last Name</label>
                                            <input
                                                type="text"
                                                className="w-full bg-[#2b3139] border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#fcd535]"
                                                value={formData.lastName}
                                                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-400 mb-2">Email Address</label>
                                            <input
                                                type="email"
                                                disabled
                                                className="w-full bg-[#2b3139] border border-gray-700 rounded-lg px-4 py-2 text-gray-500 cursor-not-allowed"
                                                value={user?.email}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-400 mb-2">Phone Number</label>
                                            <input
                                                type="tel"
                                                className="w-full bg-[#2b3139] border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#fcd535]"
                                                value={formData.phone}
                                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                    <div className="flex justify-end">
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="bg-[#fcd535] text-black px-6 py-2 rounded-lg font-bold hover:opacity-90 transition disabled:opacity-50"
                                        >
                                            {loading ? 'Saving...' : 'Save Changes'}
                                        </button>
                                    </div>
                                </form>
                            )}

                            {/* KYC TAB */}
                            {activeTab === 'kyc' && (
                                <div className="space-y-6">
                                    <div className="flex justify-between items-center bg-[#2b3139] p-4 rounded-lg">
                                        <div>
                                            <h3 className="text-white font-medium">Current Status</h3>
                                            <p className="text-sm text-gray-400">Your identity verification status</p>
                                        </div>
                                        {renderStatusBadge(kycStatus?.status)}
                                    </div>

                                    {kycStatus?.rejectionReason && (
                                        <div className="bg-red-500/10 border border-red-500/50 p-4 rounded-lg text-red-400 text-sm">
                                            <strong>Rejection Reason:</strong> {kycStatus.rejectionReason}
                                        </div>
                                    )}

                                    {(!kycStatus || kycStatus.status === 'rejected' || kycStatus.status === 'pending') && (
                                        <form onSubmit={handleKYCSubmit} className="space-y-6 mt-6">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-400 mb-2">Country of Residence</label>
                                                    <input required type="text" className="w-full bg-[#2b3139] border border-gray-700 rounded-lg px-4 py-2 text-white"
                                                        value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })} />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-400 mb-2">City</label>
                                                    <input required type="text" className="w-full bg-[#2b3139] border border-gray-700 rounded-lg px-4 py-2 text-white"
                                                        value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} />
                                                </div>
                                                <div className="md:col-span-2">
                                                    <label className="block text-sm font-medium text-gray-400 mb-2">Full Address</label>
                                                    <textarea required className="w-full bg-[#2b3139] border border-gray-700 rounded-lg px-4 py-2 text-white" rows="2"
                                                        value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })}></textarea>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-400 mb-2">Postal Code</label>
                                                    <input required type="text" className="w-full bg-[#2b3139] border border-gray-700 rounded-lg px-4 py-2 text-white"
                                                        value={formData.postalCode} onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })} />
                                                </div>
                                            </div>

                                            <div className="border-t border-gray-800 pt-6">
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-400 mb-2">Document Type</label>
                                                        <select className="w-full bg-[#2b3139] border border-gray-700 rounded-lg px-4 py-2 text-white"
                                                            value={formData.documentType} onChange={(e) => setFormData({ ...formData, documentType: e.target.value })}>
                                                            <option value="passport">Passport</option>
                                                            <option value="national_id">National ID Card</option>
                                                            <option value="drivers_license">Driver's License</option>
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-400 mb-2">Document Number</label>
                                                        <input required type="text" className="w-full bg-[#2b3139] border border-gray-700 rounded-lg px-4 py-2 text-white"
                                                            value={formData.documentNumber} onChange={(e) => setFormData({ ...formData, documentNumber: e.target.value })} />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                                                <div className="border-2 border-dashed border-gray-700 rounded-lg p-6 text-center hover:border-[#fcd535] transition cursor-pointer relative">
                                                    <input type="file" className="absolute inset-0 opacity-0 cursor-pointer"
                                                        onChange={(e) => setFiles({ ...files, front: e.target.files[0] })} />
                                                    <Upload className="mx-auto text-gray-500 mb-2" />
                                                    <p className="text-sm text-gray-300">Front of ID</p>
                                                    {files.front && <p className="text-xs text-[#fcd535] mt-1">{files.front.name}</p>}
                                                </div>
                                                <div className="border-2 border-dashed border-gray-700 rounded-lg p-6 text-center hover:border-[#fcd535] transition cursor-pointer relative">
                                                    <input type="file" className="absolute inset-0 opacity-0 cursor-pointer"
                                                        onChange={(e) => setFiles({ ...files, back: e.target.files[0] })} />
                                                    <Upload className="mx-auto text-gray-500 mb-2" />
                                                    <p className="text-sm text-gray-300">Back of ID</p>
                                                    {files.back && <p className="text-xs text-[#fcd535] mt-1">{files.back.name}</p>}
                                                </div>
                                                <div className="border-2 border-dashed border-gray-700 rounded-lg p-6 text-center hover:border-[#fcd535] transition cursor-pointer relative">
                                                    <input type="file" className="absolute inset-0 opacity-0 cursor-pointer"
                                                        onChange={(e) => setFiles({ ...files, selfie: e.target.files[0] })} />
                                                    <User className="mx-auto text-gray-500 mb-2" />
                                                    <p className="text-sm text-gray-300">Selfie with ID</p>
                                                    {files.selfie && <p className="text-xs text-[#fcd535] mt-1">{files.selfie.name}</p>}
                                                </div>
                                            </div>

                                            <div className="flex justify-end">
                                                <button
                                                    type="submit"
                                                    disabled={loading}
                                                    className="bg-[#fcd535] text-black px-6 py-2 rounded-lg font-bold hover:opacity-90 transition disabled:opacity-50"
                                                >
                                                    {loading ? 'Submitting...' : 'Submit Verification'}
                                                </button>
                                            </div>
                                        </form>
                                    )}
                                </div>
                            )}

                            {/* SECURITY TAB */}
                            {activeTab === 'security' && (
                                <form onSubmit={handlePasswordChange} className="space-y-6 max-w-md">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-400 mb-2">Current Password</label>
                                        <input required type="password" className="w-full bg-[#2b3139] border border-gray-700 rounded-lg px-4 py-2 text-white"
                                            value={passwordData.currentPassword} onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-400 mb-2">New Password</label>
                                        <input required type="password" className="w-full bg-[#2b3139] border border-gray-700 rounded-lg px-4 py-2 text-white"
                                            value={passwordData.newPassword} onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-400 mb-2">Confirm New Password</label>
                                        <input required type="password" className="w-full bg-[#2b3139] border border-gray-700 rounded-lg px-4 py-2 text-white"
                                            value={passwordData.confirmPassword} onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })} />
                                    </div>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="bg-[#fcd535] text-black px-6 py-2 rounded-lg font-bold hover:opacity-90 transition disabled:opacity-50"
                                    >
                                        {loading ? 'Updating...' : 'Update Password'}
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProfilePage;
